import { ChangeEvent, FC, MouseEvent, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from "@mui/material";
import { v4 } from "uuid";

import { TrainRecommendationSortColumn } from "@framework/types";
import { SortDirection } from "@framework/types-collection";

import { ReportDownloadButton } from "../../../../../../components/common/buttons/report-download";
import { SemaforIcon, formatDateHumanReadable } from "../../../../../../utils";
import { StyledTablePagination } from "../../ui/table-pagination";
import { useScreenSize } from "../../../../../../utils/screen";
import { SortButton } from "../../../../../../components/common/buttons/sort-button";
import { IRecommendationsResponse } from "./interfaces";
import { StyledHeaderTableCell, StyledTableCell } from "./styled";

export interface IRecommendationsTable {
  recommendations: IRecommendationsResponse[];
  handleSort: (value: TrainRecommendationSortColumn) => () => void;
  sortColumn: TrainRecommendationSortColumn;
  sortOrder: SortDirection;
}

export const RecommendationsTable: FC<IRecommendationsTable> = props => {
  const { handleSort, sortColumn, sortOrder, recommendations } = props;

  const { isExtraLargeScreen } = useScreenSize();
  const { formatMessage } = useIntl();

  const recommendationsId = "pages.scheme.recommendations";
  const headersId = `${recommendationsId}.headers`;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const rows = useMemo(
    () =>
      recommendations.map(recommendation => {
        const { Position, Recommendation, Priority, Rate, DepartmentName, PerformerName, Date, idReport } =
          recommendation;

        return {
          position: Position,
          date: formatDateHumanReadable(Date),
          name: Recommendation,
          priority: Priority,
          departmentName: DepartmentName,
          rate: Rate,
          performerName: PerformerName,
          idReport,
        };
      }),
    [recommendations],
  );

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const handleChangePage = (_event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (isExtraLargeScreen) {
      setPage(0);
      setRowsPerPage(15);
    } else {
      setPage(0);
      setRowsPerPage(5);
    }
  }, [isExtraLargeScreen]);

  return (
    <TableContainer
      elevation={0}
      component={Paper}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        borderRadius: "8px",
        border: "1px solid #EBEBF7",
      }}
    >
      <Table sx={{ minWidth: 500, width: "100%" }} aria-label="recommendations table" stickyHeader>
        <TableHead>
          <TableRow
            sx={{
              "& .MuiTableCell-root": {
                borderRight: "1px solid #EBEBF7",
                backgroundColor: "#FAFAFF",
                borderColor: "#EBEBF7",
                "&:last-of-type": {
                  borderRight: "none",
                },
              },
            }}
          >
            <StyledHeaderTableCell>
              {formatMessage({ id: `${headersId}.growthRate` })}
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainRecommendationSortColumn.Rate)}
                isActive={sortColumn === TrainRecommendationSortColumn.Rate}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>
              {formatMessage({ id: `${headersId}.position` })}
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainRecommendationSortColumn.Position)}
                isActive={sortColumn === TrainRecommendationSortColumn.Position}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>
              {formatMessage({ id: `${headersId}.date` })}
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainRecommendationSortColumn.Date)}
                isActive={sortColumn === TrainRecommendationSortColumn.Date}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>
              {formatMessage({ id: `${headersId}.name` })}
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainRecommendationSortColumn.Recommendation)}
                isActive={sortColumn === TrainRecommendationSortColumn.Recommendation}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>
              {formatMessage({ id: `${headersId}.priority` })}
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainRecommendationSortColumn.Priority)}
                isActive={sortColumn === TrainRecommendationSortColumn.Priority}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>
              {formatMessage({ id: `${headersId}.department` })}
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainRecommendationSortColumn.DepartmentName)}
                isActive={sortColumn === TrainRecommendationSortColumn.DepartmentName}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>
              {formatMessage({ id: `${headersId}.performer` })}
              <SortButton
                width={12}
                height={12}
                direction={sortOrder}
                onClick={handleSort(TrainRecommendationSortColumn.PerformerName)}
                isActive={sortColumn === TrainRecommendationSortColumn.PerformerName}
              />
            </StyledHeaderTableCell>
            <StyledHeaderTableCell>{formatMessage({ id: `${headersId}.report` })}</StyledHeaderTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(rowsPerPage > 0 ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : rows).map(row => (
            <TableRow
              key={v4()}
              sx={{
                "& .MuiTableCell-root": {
                  borderColor: "#EBEBF7",
                  borderRight: "1px solid #EBEBF7",
                  "&:last-of-type": {
                    borderRight: "none",
                  },
                },
              }}
            >
              <StyledTableCell sx={{ minWidth: 40 }}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <SemaforIcon status={row.rate} width={18} height={18} labeled />
                </Box>
              </StyledTableCell>
              <StyledTableCell sx={{ minWidth: 70 }}>{row.position}</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 70 }}>{row.date}</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 70 }}>{row.name}</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 70 }}>
                {row.priority ? formatMessage({ id: `enums.recommendationPriority.${row.priority}` }) : ""}
              </StyledTableCell>
              <StyledTableCell sx={{ minWidth: 70 }}>{row.departmentName}</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 70 }}>{row.performerName}</StyledTableCell>
              <StyledTableCell sx={{ minWidth: 40 }}>
                {row.idReport ? (
                  <ReportDownloadButton idReport={row.idReport} />
                ) : (
                  formatMessage({ id: `${recommendationsId}.noReports` })
                )}
              </StyledTableCell>
            </TableRow>
          ))}
          {emptyRows > 0 && (
            <TableRow sx={{ height: 53 * emptyRows }}>
              <TableCell colSpan={8} />
            </TableRow>
          )}
        </TableBody>
        {rows.length > rowsPerPage ? (
          <TableFooter>
            <TableRow
              sx={{
                "& .MuiToolbar-root.MuiTablePagination-toolbar": { minHeight: 30, fontSize: 12 },
                "& .MuiToolbar-root.MuiTablePagination-toolbar p": { fontSize: 12, m: 0 },
                "& .MuiToolbar-root .MuiButtonBase-root": { padding: 0.5 },
              }}
            >
              <StyledTablePagination
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                page={page}
                rowsLength={rows.length}
                rowsPerPage={rowsPerPage}
                colSpan={8}
              />
            </TableRow>
          </TableFooter>
        ) : null}
      </Table>
    </TableContainer>
  );
};
